import React, { useCallback, useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import type { IScannerControls } from "@zxing/browser";
import { AlertTriangle, ScanLine } from "lucide-react";
import { toast } from "react-toastify";

import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Input from "../ui/Input";
import SearchInput, { SearchInputOption } from "../ui/SearchInput";
import FilterSelect from "../ui/FilterSelect";

import productApis from "src/api/product";
import { Product } from "src/types/product.types";
import { formatVariantLabel } from "src/utils/common";

interface ScanProductModalProps {
  open: boolean;
  onClose: () => void;
  onProductFound: (product: Product, variantId: string) => void;
}

const ScanProductModal: React.FC<ScanProductModalProps> = ({
  open,
  onClose,
  onProductFound,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const resolvingRef = useRef(false);
  // Bumped every time a camera (re)start is attempted, and captured per-call
  // below. getUserMedia/decodeFromConstraints is async, so a call whose
  // token has since been superseded (StrictMode's mount→cleanup→mount in
  // dev, a fast "Scan again", or the modal closing mid-request) must stop
  // whatever stream IT ends up with instead of leaking it into controlsRef.
  const startTokenRef = useRef(0);

  const [cameraError, setCameraError] = useState<string | null>(null);
  const [barcodeValue, setBarcodeValue] = useState("");
  const [resolving, setResolving] = useState(false);
  const [notFoundBarcode, setNotFoundBarcode] = useState<string | null>(null);

  const [searchedProduct, setSearchedProduct] = useState<Product | null>(null);
  const [searchedVariantId, setSearchedVariantId] = useState("");

  const handleResolve = useCallback(
    async (barcode: string) => {
      const trimmed = barcode.trim();
      if (!trimmed || resolvingRef.current) return;
      resolvingRef.current = true;
      setResolving(true);
      setNotFoundBarcode(null);
      try {
        const res: any = await productApis.lookupByBarcode(trimmed);
        const { product, variantId } = res?.data || {};
        if (product) {
          onProductFound(product, variantId || "");
          onClose();
        } else {
          setNotFoundBarcode(trimmed);
          controlsRef.current?.stop();
          controlsRef.current = null;
        }
      } catch (error: any) {
        if (error?.status === 404) {
          setNotFoundBarcode(trimmed);
          controlsRef.current?.stop();
          controlsRef.current = null;
        } else {
          toast.error(error?.message || "Failed to look up barcode");
        }
      } finally {
        resolvingRef.current = false;
        setResolving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClose]
  );

  const startCamera = useCallback(async () => {
    if (!videoRef.current) return;
    const token = ++startTokenRef.current;
    // A fresh start (e.g. "Scan again") always supersedes whatever's running.
    controlsRef.current?.stop();
    controlsRef.current = null;
    try {
      const reader = new BrowserMultiFormatReader();
      const controls = await reader.decodeFromConstraints(
        { video: { facingMode: "environment" } },
        videoRef.current,
        (result) => {
          if (result && startTokenRef.current === token) handleResolve(result.getText());
        }
      );
      if (startTokenRef.current !== token) {
        // Superseded while the camera permission/negotiation was pending —
        // this stream was never assigned to controlsRef, so it's the only
        // thing that can stop it.
        controls.stop();
        return;
      }
      controlsRef.current = controls;
      setCameraError(null);
    } catch (err: any) {
      if (startTokenRef.current === token) {
        setCameraError(
          "Camera unavailable — use the field below to enter a barcode, or search for the product instead."
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleResolve]);

  useEffect(() => {
    if (!open) return;
    setBarcodeValue("");
    setNotFoundBarcode(null);
    setSearchedProduct(null);
    setSearchedVariantId("");
    startCamera();

    return () => {
      startTokenRef.current++;
      controlsRef.current?.stop();
      controlsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleBarcodeSubmit = () => {
    if (barcodeValue.trim()) handleResolve(barcodeValue);
  };

  const handleScanAgain = () => {
    setNotFoundBarcode(null);
    startCamera();
  };

  const handleAddProductRedirect = () => {
    window.open("/app/product/add", "_blank");
  };

  const isVariantProduct = searchedProduct?.productType === "variant";
  const variantOptions = (searchedProduct?.variants || []).map((v) => ({
    label: formatVariantLabel(v.optionValues) || v.sku || "Variant",
    value: v._id || "",
  }));

  const handleUseSearchedProduct = () => {
    if (!searchedProduct) return;
    if (isVariantProduct && !searchedVariantId) {
      toast.error("Select a variant first");
      return;
    }
    onProductFound(searchedProduct, searchedVariantId);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Scan product" size="xl">
      <div className="space-y-5">
        {/* Camera view */}
        <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-black">
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            muted
            playsInline
          />
          {cameraError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gray-900/95 p-4 text-center">
              <AlertTriangle size={22} className="text-amber-400" />
              <p className="text-sm text-gray-200">{cameraError}</p>
            </div>
          )}
          {!cameraError && !notFoundBarcode && (
            <div className="pointer-events-none absolute inset-6 rounded-lg border-2 border-white/70" />
          )}
        </div>

        {/* Manual barcode entry — also how a physical keyboard-wedge scanner resolves */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Enter barcode
          </label>
          <div className="flex gap-2">
            <Input
              autoFocus
              placeholder="Scan or type a barcode"
              value={barcodeValue}
              onChange={(e) => setBarcodeValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleBarcodeSubmit();
                }
              }}
              disabled={resolving}
              className="flex-1"
            />
            <Button
              onClick={handleBarcodeSubmit}
              isLoading={resolving}
              disabled={resolving || !barcodeValue.trim()}
            >
              Look up
            </Button>
          </div>
        </div>

        {notFoundBarcode && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-900/20">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              No product found for barcode "{notFoundBarcode}"
            </p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" onClick={handleAddProductRedirect}>
                Add product
              </Button>
              <Button size="sm" variant="outline" onClick={handleScanAgain}>
                <ScanLine size={14} className="mr-1" />
                Scan again
              </Button>
            </div>
          </div>
        )}

        {/* OR divider */}
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            OR
          </span>
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        </div>

        {/* Search product instead */}
        <div>
          <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
            Search product instead
          </h3>
          <div className="space-y-3">
            <SearchInput<Product>
              placeholder="Search by name, SKU, HSN, or product ID..."
              value={
                searchedProduct
                  ? `${searchedProduct.name} (${searchedProduct.sku || searchedProduct.productId})`
                  : ""
              }
              onSearch={async (query) => {
                const res = await productApis.getProductList({
                  page: 1,
                  limit: 10,
                  status: "active",
                  search: query,
                });
                const list: Product[] = res?.data?.data || res?.data || [];
                return list.map((p) => ({
                  value: p._id || "",
                  label: `${p.name} (${p.sku || p.productId})`,
                  data: p,
                })) as SearchInputOption<Product>[];
              }}
              onSelect={(option: SearchInputOption<Product>) => {
                setSearchedProduct(option.data);
                setSearchedVariantId("");
              }}
              onClear={() => {
                setSearchedProduct(null);
                setSearchedVariantId("");
              }}
              renderOption={(option) => (
                <div>
                  <div className="font-medium text-gray-800 dark:text-gray-100">
                    {option.data.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    SKU: {option.data.sku || "-"} · HSN:{" "}
                    {option.data.hsnCode || "-"}
                  </div>
                </div>
              )}
            />

            {isVariantProduct && (
              <FilterSelect
                variant="boxed"
                label="Variant"
                value={searchedVariantId}
                onChange={setSearchedVariantId}
                allLabel="Select variant"
                options={variantOptions}
              />
            )}

            {searchedProduct && (
              <div className="flex justify-end">
                <Button size="sm" onClick={handleUseSearchedProduct}>
                  Use this product
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ScanProductModal;
